/** @module components/lyst-app */
import baustein from "baustein";
import _each from "lodash/each";
import _debounce from "lodash/debounce";
import { watchState } from "web/redux/state-watcher";
import analytics from "web/script/analytics/analytics";
import facebookEvents from "web/script/analytics/facebook-events";
import { events as calloutEvents } from "web/script/components/app-header-callout";
import ProductOverlay from "web/script/components/overlays/product-overlay";
import ShippingAndReturnsOverlay from "web/script/components/overlays/shipping-and-returns-overlay";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import requester from "web/script/modules/requester";
import Stack from "web/script/modules/stack";
import { getProductPageRegex } from "web/script/routing/routes";
import alerts from "web/script/utils/alerts";
import browser from "web/script/utils/browser";
import globalEvents from "web/script/utils/global-events";
import history from "web/script/utils/history";
import logging from "web/script/utils/logging";
import url from "web/script/utils/url";

import "web/script/components/background-banner";
import "web/script/components/layouts/default-layout";
// Footer
import "web/script/components/tracked-link";

const LYST_APP_CALLOUT_SHOWN_MODIFIER = "lyst-app--callout-shown";
const LAYOUT_CONTAINER_SELECTOR = ".lyst-app__layout-container";
const LAYOUT_CONTAINER_DIMMED_MODIFIER = "lyst-app__layout-container--dimmed";
const LAYOUT_MATTE_SELECTOR = ".lyst-app__matte";
const APP_HEADER_COMPONENT = "app-header";

/**
 * This component encapsulates the entire lyst web app. It handles page load set up, any
 * subsequent client side navigation, and ideally also will manage the lifecycle of overlays.
 * @constructor
 * @alias module:components/lyst-app
 * @extends module:baustein
 */
export default baustein.register(
    "lyst-app",
    /** @lends module:components/lyst-app.prototype */
    {
        defaultOptions: {
            surveyEnabled: false,
            exitOverlaySource: "",
            exitOverlayUrl: "",
        },

        overlays: ["base-overlay", "country-overlay", "editing-overlay", "product-overlay"],

        membershipPages: ["sign_in", "new_signup", "signup", "signup form"],

        setupEvents(add) {
            add("overlayOpen", this._onOverlayOpen);
            add("overlayCloseIntent", this._onOverlayCloseIntent);
            add("overlayClose", this._onOverlayClose);
            add("searchOpened", this.onSearchOpen);
            add("searchClosed", this.onSearchClose);
            add("addedToBag", this.openCartCallout);
            add("appHeaderUpdatedOffset", this.onHeaderUpdatedOffset);
            add("desktopHeaderUpdatedOffset", this.onHeaderUpdatedOffset);
            add("oosItemFound", "cart-summary", this.onCartCalloutOpened);
            add("saveForLaterButtonInserted", this._onSaveForLaterButtonInserted);
            add("cartSummaryShown", this.closeSaveForLaterCallout);
            add("showCsaUnit", this.showCsaUnit);
            add("showCsaShoppingUnit", this.showCsaShoppingUnit);
            add("fixPageContent", this.fixPageContent);
            add("unFixPageContent", this.unFixPageContent);
            add("showInstantFeedback", this.showInstantFeedback);
            add("click", LAYOUT_MATTE_SELECTOR, this.onLayoutMatteClick);
            [
                "appHeaderMenuOpened",
                "appHeaderAutoCompleteOpened",
                calloutEvents.OPEN_LYST_MATTE,
            ].forEach((e) => add(e, this.addLayoutMatteAndBlockScroll));
            add(calloutEvents.OPEN_LYST_MATTE_WITH_SCROLL, this.addLayoutMatte);
            [
                "appHeaderMenuClosed",
                "appHeaderAutoCompleteClosed",
                calloutEvents.CLOSE_LYST_MATTE,
            ].forEach((e) => add(e, this.removeLayoutMatte));
        },

        init() {
            this.attachShippingOverlayWatcher();
            this.attachAppHeaderCartWatcher();
            this.crmSurveyOverlay;
            this.overlayStack = new Stack();
            this.sflProductIdsQueue = [];

            if (environment.get("facebookEvent")) {
                // we have a facebook page event to send
                this._trackFacebookEvent();
            }

            this._trackPWAAppInstallEvent();
            this._trackPWAHomescreenLaunch();
            this._trackPWAMode();
            this._performFrontEndRouting();
            this._redirectProxySites();
        },

        onInsert() {
            this._addSupportClasses();

            // these are messages created using the Django messages framework
            // See https://docs.djangoproject.com/en/1.8/ref/contrib/messages/ for more info
            _each(environment.get("messages"), alerts.alert);

            const parsedURL = url.parse(globals.window.location);

            // show instant notification if registered = "true" and area are set
            const notificationArea = parsedURL.searchParams.get("area");
            if (
                (parsedURL.searchParams.get("loggedin") === "true" ||
                    parsedURL.searchParams.get("registered") === "true") &&
                notificationArea
            ) {
                this.showInstantFeedback({ area: notificationArea });
            }

            // fire stock check on page load
            const header = this.findComponent(APP_HEADER_COMPONENT);
            if (header) {
                const cartButton = header.findComponent("app-header-cart");

                if (cartButton) {
                    cartButton.checkStock();
                }

                let stickyBuyButton = this.findComponent("buybuybuy-sticky-buy-button");

                if (stickyBuyButton) {
                    stickyBuyButton.setHeaderHeightReference(
                        header.el.getBoundingClientRect().height
                    );
                }
            }
        },

        onRemove() {
            this.releaseGlobalHandler("mouseleave", this.onBodyMouseLeave);
        },

        /**
         * Block the scrolling of the body.
         */
        blockScrolling() {
            this.el.classList.add("no-scroll");
        },

        /**
         * Un-block the scrolling of the body.
         */
        unblockScrolling() {
            this.el.classList.remove("no-scroll");
        },

        /**
         * A function to reference when we want to prevent a default browser behaviour
         * @param {Event} event
         */
        preventDefault(event) {
            event.preventDefault();
            return false;
        },
        fixPageContent() {
            this.el.classList.add("fix-element");
        },

        unFixPageContent() {
            this.el.classList.remove("fix-element");
        },

        /**
         * Tracks Facebook events.
         * @private
         */
        _trackFacebookEvent() {
            var event = environment.get("facebookEvent");
            analytics.tagEvent(analytics.TAG_NETWORKS.FACEBOOK, facebookEvents[event]);
        },

        /**
         * Tracks PWA appinstalled event.
         * @private
         */
        _trackPWAAppInstallEvent() {
            window.addEventListener("appinstalled", function() {
                analytics.event("pwa", "installed");
            });
        },

        /**
         * Tracks if user launched PWA from homescreen
         * @private
         */
        _trackPWAHomescreenLaunch() {
            var params = this._getParamsFromRequest();
            if (params.get("utm_medium") === "pwa") {
                analytics.event("pwa", "homescreen_launch");
            }
        },

        /**
         * Tracks if user is currently in PWA
         * @private
         */
        _trackPWAMode() {
            var isPWAinBrowser = true;

            if (matchMedia("(display-mode: standalone)").matches) {
                // Android and iOS 11.3+
                isPWAinBrowser = false;
            } else if ("standalone" in navigator) {
                // useful for iOS < 11.3
                isPWAinBrowser = !navigator.standalone;
            }

            if (!isPWAinBrowser) {
                analytics.event("pwa", "is_pwa");
            }
        },

        /**
         * If the page is being loaded on a non-lyst domain, redirect back to Lyst.
         * @private
         */
        _redirectProxySites() {
            // Attempt to guess if this is an allowed domain.
            const hostname = document.location.hostname;
            const href = document.location.href;
            const hostWhitelist = /(lyst|127\.0\.0\.1|localhost|archive\.org|google)/;
            const hrefWhitelist = /translate.*\/.*\?.*=.*lyst/;

            if (hostname.match(hostWhitelist) || href.match(hrefWhitelist)) {
                return;
            } else {
                document.location.href = `https://www.lyst.com${document.location.pathname}?nonlystdomainredirect=1`;
            }
        },

        _getParamsFromRequest() {
            var parsed = url.parse(globals.window.location.href);
            return parsed.searchParams;
        },

        /**
         * Adds classes to this component that indicate whether certain
         * features are supported or not.
         * @private
         */
        _addSupportClasses() {
            var classList = this.el.classList;

            // add class to the body to indicate whether this device has touch support
            classList.add(browser.hasTouch ? "has-touch" : "no-touch");

            // if device supports css transforms add class to body for CSS to hook into
            classList.toggle("has-css-transforms", browser.supportsCSSTransforms);

            // if device has css transitions
            classList.toggle("no-css-transitions", !browser.supportsCSSTransitions);

            // If scrollbars are stylable, add a class to help.
            classList.toggle("css-scrollbars", browser.supportsCSSScrollbars);
        },

        getLayoutContainer() {
            return this.el.querySelector(LAYOUT_CONTAINER_SELECTOR);
        },

        hideContent() {
            this.getLayoutContainer().classList.add("hidden");
            this.el.querySelector(".background-banner-wrapper").classList.add("hidden");
        },

        showContent() {
            this.getLayoutContainer().classList.remove("hidden");
            this.el.querySelector(".background-banner-wrapper").classList.remove("hidden");
        },

        openCartCallout() {
            // Increment cart button item count by 1
            globalEvents.trigger("cart.change", { itemCountDifference: 1 });

            const header = this.findComponent(APP_HEADER_COMPONENT);

            for (var cartButton of header.findComponents("app-header-cart")) {
                // show cart callout
                cartButton.showCartCallout();
            }
            analytics.event("header", "show-cart-callout", null, true);

            this.onCartCalloutOpened();
        },

        onCartCalloutOpened() {
            // show desktop header, add matte, prevent scrolling
            this.invoke(this.findComponent("desktop-header"), "show");
            this.addLayoutMatteAndBlockScroll();
            this.el.classList.add(LYST_APP_CALLOUT_SHOWN_MODIFIER);

            // close existing callouts
            this.closeSaveForLaterCallout();
        },

        onHeaderUpdatedOffset(event) {
            const { offset } = event;
            const stickyBuyButton = this.findComponent("buybuybuy-sticky-buy-button");

            if (stickyBuyButton) {
                stickyBuyButton.updatePosition(offset);
            }
        },

        onSearchOpen() {
            this.getLayoutContainer().classList.add("hidden");
            this.el.classList.add("lyst-app--full-height");
        },

        onSearchClose() {
            this.getLayoutContainer().classList.remove("hidden");
            this.el.classList.remove("lyst-app--full-height");
        },

        closeSaveForLaterCallout() {
            const header = this.findComponent(APP_HEADER_COMPONENT);

            if (!header) {
                return;
            }

            const saveForLaterComponent = header.findComponent("save-for-later-callout");
            if (saveForLaterComponent) {
                saveForLaterComponent.destroy();
            }
        },

        closeCartSummary() {
            const header = this.findComponent(APP_HEADER_COMPONENT);

            if (header) {
                const cartSummary = header.findComponent("cart-summary");
                this.invoke(cartSummary, "close");
            }
        },

        addLayoutMatte() {
            const lystAppContainer = this.getLayoutContainer();

            if (lystAppContainer) {
                lystAppContainer.classList.add(LAYOUT_CONTAINER_DIMMED_MODIFIER);
            }

            this.closeSaveForLaterCallout();
        },

        removeLayoutMatte() {
            const lystAppContainer = this.getLayoutContainer();

            if (lystAppContainer) {
                lystAppContainer.classList.remove(LAYOUT_CONTAINER_DIMMED_MODIFIER);
            }

            this.unblockScrolling();
        },

        addLayoutMatteAndBlockScroll() {
            this.addLayoutMatte();
            this.blockScrolling();
        },

        _onOverlayOpen(event) {
            this.overlayStack.push(event.target);
            this.blockScrolling();
        },

        _onOverlayCloseIntent(event) {
            const activeOverlay = this.overlayStack.peek();

            // If the event is not coming from the active
            // overlay, preventDefault and do nothing.
            if (activeOverlay !== event.target) {
                event.preventDefault();
                return;
            }
        },

        _onOverlayClose() {
            const header = this.findComponent("app-header");

            this.overlayStack.pop();

            // If there is no active overlay,
            // unblock the scrolling
            if (this.overlayStack.isEmpty()) {
                if (header && header.openCallouts) {
                    return;
                }

                this.unblockScrolling();
                this.unFixPageContent();
            }
        },

        closeSaveForLaterCallout() {
            const callout = this.findComponent("save-for-later-callout");
            if (callout) {
                callout.destroy();
            }
        },

        /**
         * On every save for later button insert event, pushes the product ID
         * into a list. Then fetches the pins and re-render the SFL button
         * if it has to.
         *
         * @param {Event} event
         */
        _onSaveForLaterButtonInserted(event) {
            this.sflProductIdsQueue.push(event.productId);
            this._fetchSaveForLaterPins();
        },

        /**
         * Requests the pins API endpoint passing the list of product ids in the
         * page. That will return an object with a map of the ids to their pin
         * status, i.e.
         *      {
         *          00000: True,  # The user has saved/pinned this product...
         *          00101: False, # ... But not this one.
         *          01010: True   # etc.
         *      }
         * Runs every 50ms – debounced.
         */
        _fetchSaveForLaterPins: _debounce(function() {
            const endpoint = "/services/pins/";
            const query = url.toURLSearchParams({
                product_id: [...this.sflProductIdsQueue],
            });

            this.sflProductIdsQueue = [];

            requester
                .get(endpoint, query)
                .then((data) => {
                    this._setPinnedProducts(data.pin_mapping);
                })
                .catch((error) => logging.error(error));
        }, 50),

        /**
         * Loops through the save for later buttons in the page and re-renders them
         * based on the user-saved product pins.
         *
         * @param {Object} pinnedProductsMapping - The product id mapping
         */
        _setPinnedProducts(pinnedProductsMapping) {
            const sflButtons = this.findComponents("save-for-later-button");
            sflButtons.forEach((saveForLaterButton) => {
                const isPinned = pinnedProductsMapping[saveForLaterButton.options.productId];
                if (isPinned) {
                    saveForLaterButton._render(true);
                }
            });
        },

        showCsaUnit(event) {
            const position = event["position"];
            const csaContainers = this.findComponents("adsense-custom-search-ads");
            csaContainers.forEach((csaContainer) => {
                if (csaContainer.options.dataPosition == position) {
                    csaContainer.createCsaUnit();
                }
            });
        },

        showCsaShoppingUnit(event) {
            const position = event["position"];
            const csaContainers = this.findComponents("adsense-custom-search-ads");
            csaContainers.forEach((csaContainer) => {
                if (csaContainer.options.dataPosition == position) {
                    csaContainer.createCsaShoppingUnit();
                }
            });
        },

        onLayoutMatteClick() {
            this.removeLayoutMatte();

            const savedSearchCreatorComponent = this.findComponent("saved-search-creator");
            if (savedSearchCreatorComponent) {
                savedSearchCreatorComponent.closeDropDownsAndBlurElements();
            }
        },

        showInstantFeedback({ area }) {
            this.findComponents("instant-feedback").forEach((component) => {
                if (component.options.area === area) {
                    component.show();
                }
            });
        },

        attachAppHeaderCartWatcher() {
            watchState(
                (state) => {
                    return state.appHeaderCartReducer;
                },
                () => {
                    this.openCartCallout();
                }
            );
        },

        attachShippingOverlayWatcher() {
            watchState(
                (state) => {
                    return state.shippingAndReturnsOverlayReducer;
                },
                (overlayState) => {
                    if (!overlayState.open) {
                        return;
                    }
                    const linkId = overlayState.linkId;
                    const shippingAndReturnsOverlay = new ShippingAndReturnsOverlay({ linkId });

                    // append overlay to the document body, because this component will typically
                    // be shown in an accordion, which is position relative and overflow hidden
                    // so would not display correctly
                    shippingAndReturnsOverlay.appendTo(document.body);
                    shippingAndReturnsOverlay.open();
                    analytics.event("buy_area", "show_shipping_and_returns");
                }
            );
        },

        _performFrontEndRouting() {
            history.on("change", this._onHistoryChange.bind(this));
        },

        /**
         * @param {import("web/script/utils/history").BrowserHistoryEvent} data
         */
        async _openProductOverlay({ newURL }) {
            let { pathname, searchParams } = newURL;
            let [, , productSlug] = pathname.match(getProductPageRegex());
            let args = {
                productSlug,
                // TODO: These were always undefined before, is fixing them correct?
                productUid: searchParams.get("product_overlay_uid"),
                linkId: searchParams.get("link_id"),
            };

            let existingOverlay = this.findComponent("product-overlay");
            if (existingOverlay) {
                return existingOverlay.updateOptions(args);
            }

            // NOTE: Change this to an `import()` statement to bundle split the overlay
            let overlay = new ProductOverlay(args);
            overlay.appendTo(this);
            return overlay.open();
        },

        /**
         * Handle the remnants of our frontend routing system.
         * Currently it is used for 2 things:
         *  - Opening the product overlay
         *  - Updating feeds (pagination & filters)
         * All other routing is done via full page changes.
         * @param {never} _
         * @param {import("web/script/utils/history").BrowserHistoryEvent} data
         */
        async _onHistoryChange(_, data) {
            // If we're not changing any page parameters (eg opening a
            // non-product modal) then don't do any routing
            if (data.newURL.pathname == data.oldURL?.pathname && data.queryChanges.length == 0) {
                return;
            }

            // Don't send pageView events if you are going backwards or forwards.
            // This makes our analytics more consistent but also happens to work around
            // a problem in the reffer tracking in the analytics module that results
            // in pages having themselves as a referrer currently.
            function sendPageView() {
                if (data.eventType != "popstate") {
                    analytics.pageView();
                }
            }

            let productPageRoute = getProductPageRegex();
            let path = data.newURL.pathname;

            if (productPageRoute.test(path)) {
                // Entering a product page URL

                // If the product page component exists, we're already on a
                // product page and are moving around it (eg gallery or colour
                // variant picker) so do nothing
                if (this.findComponent("product-page")) {
                    return;
                }

                // Open or update the product overlay
                await this._openProductOverlay(data);
                sendPageView();
            } else if (productPageRoute.test(data.oldURL?.pathname)) {
                // Leaving a product page URL

                // This should only ever happen when you close an overlay.
                let overlay = this.findComponent("product-overlay");
                if (!overlay) {
                    // "this should never happen"
                    logging.error(`Unexpected product transition to ${path}?!`);
                    // The router's default "halp" reaction is to reload the page, so
                    globals.window.location.reload();
                    return;
                }
                await overlay.close();
                // No page view here (?)
            } else {
                let feedLayout = this.findComponent("product-feed-layout");
                if (!feedLayout) {
                    // "this really should never happen"
                    logging.error(`Unexpected page transition to ${path}?!`);
                    // fallback
                    globals.window.location.reload();
                    return;
                }
                await feedLayout.onFilterUpdate(_, data);
                sendPageView();
            }
        },
    }
);
